import map from "lodash/map";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import submit from "submit-form";
import { AthleteLinks } from "../../../parts/athlete-links";
import CycleCard from "../../../parts/cards/cycle-card";
import ProgramCard from "../../../parts/cards/program-card";
import ImgComponent from "../../../parts/ImgComponent";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import DropdownIconsCheck from "../../../parts/inputs/dropdown-icons-check";
import Pagination from "../../../parts/pagination";
import SvgIcon from "../../../parts/SvgIcon";
import TeamCard from "../../../parts/TeamCard";
import { RillContext } from "../../../rill/RillReact/share/base";
import {
  authoredWeeks,
  resolveFlexibleOccurrence,
} from "../../../utils/flexible-program";
import getAvatarUrl from "../../../utils/get-avatar-url";
import getCycleCardImagePath from "../../../utils/get-cycle-card-image-path";
import { clearProgramFetch } from "../../coaching-portal/common-components/program-fetch-entry";
import FlexibleSettings from "../../coaching-portal/flexible-program/components/FlexibleSettings";
import FlexibleStop from "../../coaching-portal/flexible-program/components/FlexibleStop";
import FlexibleHowItWorks from "./components/FlexibleHowItWorks";

const pageSize = 40;

const getSortItems = (t) => [
  {
    title: t("programs.dateAdded"),
    src: "/img/svg/arrow-to-bottom.svg",
    alt: "createdDate",
    className: "icon-white",
    id: "createdDate",
  },
  {
    title: t("programs.aToZ"),
    src: "/img/svg/sort-amount-up.svg",
    alt: "name",
    className: "icon-white",
    id: "name",
  },
  {
    title: t("programs.zToA"),
    src: "/img/svg/sort-amount-down.svg",
    alt: "nameDescending",
    className: "icon-white",
    id: "nameDescending",
  },
];

class View extends React.Component {
  render() {
    const { t } = this.props;
    const { locals } = this.context;
    const referAsClients = Boolean(locals.activeUser.referAsClients);
    const athlete = locals.athlete;
    const pathname = locals.pathname;
    const query = locals.query;
    const sortItems = getSortItems(t);
    const flexible = locals.athleteFlexibleProgram?.[0];
    const entity = referAsClients
      ? t("programs.client")
      : t("programs.athlete");
    return (
      <main className="athlete-programs">
        <div className="athlete-section-header only-show-mobile">
          <AthleteLinks
            athlete={athlete}
            pathName={pathname}
            classNames={"only-show-mobile"}
            query={query}
          />
          <div className="athlete-select dropdown-athlete">
            <div
              className="avatar"
              style={{
                backgroundImage: `url(${getAvatarUrl(athlete)})`,
              }}
            />
            <AthleteSelect locals={locals} />
          </div>
        </div>
        {/* header for desktop */}
        <header className="force-mobile-hidden">
          <ProgramMembership locals={locals} t={t} />
          <div>
            <DropdownIconsCheck
              items={sortItems}
              action={(sort) => {
                submit(`/actions/refresh`, {
                  method: "GET",
                  body: { sort },
                });
              }}
              title={t("programs.sort")}
            />
            <HeaderAnchorsPrograms referAsClients={referAsClients} />
          </div>
        </header>
        {/* header for mobile */}
        <header className="force-only-show-mobile">
          <div className="header-anchors-programs">
            <HeaderAnchorsPrograms referAsClients={referAsClients} />
          </div>
          <div className="header-legends-programs">
            <DropdownIconsCheck
              items={sortItems}
              action={(sort) => {
                submit(`/actions/refresh`, {
                  method: "GET",
                  body: { sort },
                });
              }}
              title={t("programs.sort")}
            />
          </div>
        </header>
        {/* Flexible only exists as a section at all once the client has one -
            there is nothing to introduce otherwise. Its header lives INSIDE
            FlexibleProgramCard's own <section> (as a sibling of .columns,
            not a wrapper around it) - Templates.css styles this card via
            `main.athlete-programs > section > .columns > article`, a direct-
            child chain. Wrapping that section in another one here would
            leave .columns two levels deep instead of one and silently drop
            every one of those rules. */}
        {flexible && (
          <FlexibleProgramCard
            flexible={flexible}
            athlete={athlete}
            organizationCoach={query.organizationCoach}
            t={t}
          />
        )}

        {/* .columns and Pagination stay immediate children of this section
            (no wrapper div around them) so the card grid keeps picking up
            Templates.css (main.athlete-programs > section > .columns >
            article). */}
        <section className="regular-program-section">
          <header className="athlete-programs-section-header">
            <h2>{t("programs.regularSectionTitle")}</h2>
            <p className="p4 opacity-70">
              {t("programs.regularSectionDescription", { entity })}
            </p>
          </header>

          {/* Pushing new regular programs while Flexible is active is allowed
              but not recommended - the client already holds one 1:1 program
              (user.activeTeam is singular), so a push lands as secondary. */}
          {flexible && (
            <div className="regular-programs-disabled-notice">
              <SvgIcon
                useCurrentColor
                src="/img/svg/exclamation-circle.svg"
                classNames="regular-programs-disabled-notice-icon"
              />
              <p>{t("programs.regularRecommendNotice", { entity })}</p>
            </div>
          )}

          <div className="columns">
            {query["program-view"] === "programs"
              ? map(this.props.cards, (program, cardIndex) => {
                  return (
                    <ProgramCard
                      key={program._id}
                      program={program}
                      cardIndex={cardIndex}
                      athlete={query.athlete}
                    />
                  );
                })
              : map(this.props.cards, (cycle, cardIndex) => {
                  return (
                    <CycleCard
                      key={cycle._id}
                      cycle={cycle}
                      cardIndex={cardIndex}
                    />
                  );
                })}
          </div>
          <Pagination
            total={this.props.total}
            pageSize={this.props.pageSize || pageSize}
          />
        </section>
      </main>
    );
  }
}

View.contextType = RillContext;

export default withTranslation("athlete-portal")(View);

/**
 * What the client is currently enrolled in, or the ways to enrol them.
 *
 * A client holds one of these at a time - `user.activeTeam` is singular - so a
 * flexible program and a team are mutually exclusive, and the entry points only
 * appear when they are on neither. The flexible program itself has its own
 * pinned card (FlexibleProgramCard, rendered separately, above the programs
 * grid) rather than living here - this only needs to know whether one exists,
 * to keep "Start Flexible Program" from being offered twice.
 */
const ProgramMembership = ({ locals, t }) => {
  const team = locals.athleteTeam?.[0];
  const flexible = locals.athleteFlexibleProgram?.[0];

  if (team) return <TeamCard team={team} />;

  if (team || flexible) return null;

  return (
    <div className="flex gap-2 items-center">
      <button
        type="button"
        data-button-type="outline"
        className="gap-2 items-center"
        onClick={() => {
          submit("/actions/refresh", {
            method: "GET",
            body: { "modal[type]": "invite-to-team" },
          });
        }}
      >
        <SvgIcon src="/img/new-icons/person-add.svg" noFilterOnLight={true} />
        <span>{t("profile.inviteToTeam")}</span>
      </button>
      {/* Flexible Programs are launching soon but not ready for clients to
          start yet - disabled (rather than removed) so the entry point stays
          visible ahead of launch. FlexibleHowItWorks still explains what's
          coming. Remove the `disabled` attribute once Flexible Programs are
          ready. */}
      <button
        type="button"
        data-button-type="outline"
        className="gap-2 items-center flexible-start-action-disabled"
        onClick={() => {
          submit("/actions/refresh", {
            method: "GET",
            body: { "modal[type]": "start-flexible-program" },
          });
        }}
      >
        <SvgIcon
          src="/img/new-icons/el-rachito-loco-che.svg"
          noFilterOnLight={true}
        />
        <span>{t("flexible.startAction")}</span>
      </button>
      <FlexibleHowItWorks />
      <span className="flexible-coming-soon-badge">{t("flexible.beta")}</span>
    </div>
  );
};

/**
 * The client's Flexible Program, pinned above the programs grid rather than
 * folded into it - flexible cycles are deliberately excluded from
 * athleteCycles/athleteCyclesCount (js/load/cycle.js) so they never show up
 * there anyway, and the program is meant to read as the primary thing for
 * this client, not one card among many.
 *
 * Built from the EXACT SAME markup a team's card uses (article.column >
 * .Cycle-Builder-Preview > .image/.details/ul.programs), not a bespoke
 * lookalike - that DOM only picks up its styling from deeply-scoped selectors
 * in Templates.css (`main.athlete-programs > section > .columns > article
 * .Cycle-Builder-Preview ...`), so reusing the real classes is what actually
 * makes it look like a card instead of approximating one. Trimmed to what a
 * 1:1 program has: a thumbnail, when it started, and exactly two actions
 * (Program Manager, Settings) - no member count, no publish switch.
 *
 * Settings opens as a local modal right here rather than navigating - it
 * shares the identical FlexibleSettings component the manager page's own
 * Settings button opens, just handed this page's `flexible`/`athlete` instead
 * of `locals.editCycle`.
 */
const FlexibleProgramCard = ({ flexible, athlete, organizationCoach, t }) => {
  const [showSettings, setShowSettings] = useState(false);
  const [showStop, setShowStop] = useState(false);
  if (!flexible) return null;

  const image = getCycleCardImagePath(flexible);
  const startedOn = flexible.programs?.[0]?.startDate
    ? moment(flexible.programs[0].startDate, "YYYYMMDD").format("MMM D, YYYY")
    : null;
  // organizationCoach has to ride along: ensure-coach.js only lets a coach
  // into an athlete that isn't on their own roster (owner / admin viewing an
  // assigned coach's client) when it's present, and otherwise redirects
  // silently to the athlete list.
  const managerPath = `/athlete-portal/programs/flexible-program/${flexible._id}?athlete=${athlete?._id}${organizationCoach ? `&organizationCoach=${organizationCoach}` : ""}`;

  // "N weeks released" rather than a week number: the live week isn't always
  // the client's first (coaches post new ones over the program's life), so
  // a count of what has been authored so far reads correctly at any point,
  // same reasoning authoredWeeks itself documents. "So far" means STARTED,
  // not just authored - a week the coach queued ahead of time (startDate in
  // the future) hasn't actually reached the client yet, so it doesn't count
  // as released until its own start date arrives.
  const today = Number(moment().format("YYYYMMDD"));
  const weeksReleased = authoredWeeks(flexible).filter(
    (week) => Number(week.startDate) <= today
  ).length;
  const live = resolveFlexibleOccurrence(flexible, today);
  const isRepeating =
    live?.status === "active" && live.program?.repeatActiveWeek !== false;
  const statusSubtitle = weeksReleased
    ? isRepeating
      ? t("flexible.cardSubtitleRepeating", { count: weeksReleased })
      : t("flexible.cardSubtitleFixed", { count: weeksReleased })
    : null;

  // A plain `window.location` assignment, not submit-form's GET-form helper:
  // a GET form submission REPLACES the action URL's own query string with the
  // form's (empty, here) field set, silently dropping ?athlete=... A guard on
  // the anchor click (rather than stopping its propagation) means a keyboard/
  // screen-reader activation of the link still works exactly as a plain <a>.
  const goToManager = (event) => {
    if (event.target.closest("a")) return;
    window.location.assign(managerPath);
  };

  return (
    <section className="flexible-pinned-card-section">
      <header className="athlete-programs-section-header">
        <h2>{t("flexible.sectionTitle")}</h2>
        <p className="p4 opacity-70">{t("flexible.sectionDescription")}</p>
      </header>
      <div className="columns">
        <article className="column pb-0!" data-col="1/3">
          <div className="Cycle-Builder-Preview">
            <div className="image" onClick={goToManager}>
              <ImgComponent
                image={image}
                classNames={`program-image ${image.startsWith("data:image") ? "no-image" : ""}`}
                altText="program thumbnail"
              />
              <span className="flexible-card-badge">
                {t("flexible.card.badge")}
              </span>
            </div>
            <div className="details">
              <header>
                <h2 className="title">{flexible.name}</h2>
                {statusSubtitle && (
                  <p className="p4 opacity-70 flexible-card-status">
                    {statusSubtitle}
                  </p>
                )}
              </header>
              {startedOn && (
                <div className="teams-details">
                  <div className="team-detail">
                    <p>{t("flexible.card.startedOn")}</p>
                    <p>{startedOn}</p>
                  </div>
                </div>
              )}

              {/* A CHILD of .details, not a sibling - like the team card's own
                  action list, this is the only ancestry Templates.css's
                  .tr-content/.tr-icon-group rules actually match; outside it
                  the rows render as plain unstyled stacked text. */}
              <ul className="programs alt-short max-h-[unset]!">
                <li
                  className="tr-Cycle-Program-Item cursor-pointer"
                  onClick={goToManager}
                >
                  <div className="tr-content">
                    <div className="tr-title">
                      {t("flexible.card.programManager")}
                    </div>
                  </div>
                  <div className="tr-icon-group">
                    <a
                      role="button"
                      data-button-type="secondary"
                      data-icon
                      data-icon-only
                      className="edit"
                      href={managerPath}
                    >
                      <SvgIcon
                        className="yellow"
                        src="/img/new-icons/vuesax-linear-document-text.svg"
                        alt="edit"
                      />
                    </a>
                  </div>
                </li>
                <li
                  className="tr-Cycle-Program-Item cursor-pointer"
                  onClick={() => setShowSettings(true)}
                >
                  <div className="tr-content">
                    <div className="tr-title">
                      {t("flexible.card.settings")}
                    </div>
                  </div>
                  <div className="tr-icon-group">
                    <button
                      type="button"
                      data-button-type="secondary"
                      data-icon
                      data-icon-only
                      className="edit"
                    >
                      <SvgIcon
                        className="yellow"
                        src="/img/new-icons/settings.svg"
                        alt={t("flexible.card.settings")}
                      />
                    </button>
                  </div>
                </li>
                <li
                  className="tr-Cycle-Program-Item cursor-pointer"
                  onClick={() => setShowStop(true)}
                >
                  <div className="tr-content">
                    <div className="tr-title">{t("flexible.card.stop")}</div>
                  </div>
                  <div className="tr-icon-group">
                    <button
                      type="button"
                      data-button-type="secondary"
                      data-icon
                      data-icon-only
                      className="edit"
                    >
                      <img
                        src="/img/new-icons/red-trash.svg"
                        alt={t("flexible.card.stop")}
                      />
                    </button>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </article>
      </div>

      {showSettings && (
        <FlexibleSettings
          cycle={flexible}
          athlete={athlete?._id}
          onClose={() => setShowSettings(false)}
        />
      )}
      {showStop && (
        <FlexibleStop
          cycleId={flexible._id}
          cycleName={flexible.name}
          athlete={athlete?._id}
          onClose={() => setShowStop(false)}
        />
      )}
    </section>
  );
};

const HeaderAnchorsPrograms = ({ referAsClients }) => {
  const { t } = useTranslation("athlete-portal");
  const entity = referAsClients ? t("programs.client") : t("programs.athlete");
  return (
    <>
      <a
        role="button"
        data-button-type="outline"
        href={`/actions/refresh/?modal[type]=fetch&destinationCycleId=&destinationCycleName=&destinationCycleStatus=&fetchSourceOwner=&fetchType=&fetchEntry=`}
        onClick={() => clearProgramFetch()}
      >
        <span>{t("programs.copyProgramFromAnother", { entity })}</span>
      </a>
      <a
        role="button"
        data-button-type="primary"
        href={`/actions/refresh/?modal[type]=build-step-one&cycleType=new`}
      >
        <SvgIcon noFilterOnLight={true} src="/img/new-icons/add.svg" />
        {t("programs.addNew")}
      </a>
    </>
  );
};
